import { z } from "zod";

import { CLASSIFICATION_SCOPES } from "../constants/classification-scopes.js";
import { COMPANY_SIZE_BANDS } from "../constants/company-size-bands.js";

// ── Profile type blocks (#625) ──
//
// How a profile type is recognised: one block per type, made of CONDITIONS that must
// all match (AND). A condition is met when ONE of its fields contains one of that
// field's keywords (OR between fields, OR between keywords).
//
// It replaces the classification rules of #472, where a rule was an AND of fields and
// the only way to write an OR between two fields was a second rule writing the same
// tag. The PO's qualification grid is written the other way round ("job title OR
// headline, AND company name OR summary, AND industry") and SFO alone took four
// rules to express, which nobody reading the rules list could reassemble. A block is
// that grid line for line.

/** The main category a type belongs to: the two groups of the PO's grid. */
export const PROFILE_TYPE_CATEGORIES = ["HNWI", "Institutionals"] as const;
export const ProfileTypeCategorySchema = z.enum(PROFILE_TYPE_CATEGORIES);
export type ProfileTypeCategory = z.infer<typeof ProfileTypeCategorySchema>;

export const BlockScopeSchema = z.enum(CLASSIFICATION_SCOPES);

export const BlockFieldSchema = z.object({
    scope: BlockScopeSchema,
    keywords: z.array(z.string()),
});
export type BlockField = z.infer<typeof BlockFieldSchema>;

/**
 * What a condition checks (#724).
 *
 * - `keywords`: its `fields`, any one of which must contain one of its keywords.
 * - `company_size`: the current company's LinkedIn band
 *   (`profiles.company_employee_range`), which must be one of `company_sizes`. A
 *   profile with no band never meets it.
 *
 * One flat object carries both rather than a union: a union becomes an `anyOf` in the
 * Fastify schemas, where ajv's `removeAdditional` strips the properties of whichever
 * branch it tries first. The unused list is simply empty.
 */
export const BLOCK_CONDITION_KINDS = ["keywords", "company_size"] as const;
export const BlockConditionKindSchema = z.enum(BLOCK_CONDITION_KINDS);
export type BlockConditionKind = z.infer<typeof BlockConditionKindSchema>;

export const CompanySizeBandSchema = z.enum(COMPANY_SIZE_BANDS);

export const BlockConditionSchema = z.object({
    kind: BlockConditionKindSchema,
    fields: z.array(BlockFieldSchema),
    /** The bands a `company_size` condition accepts; empty on a `keywords` one. */
    company_sizes: z.array(CompanySizeBandSchema),
});
export type BlockCondition = z.infer<typeof BlockConditionSchema>;

/**
 * A profile type with its block. Every type of the `profile_types` whitelist is
 * listed, block or not: a type with no condition is one nothing tags automatically
 * (yet), which is what the admin needs to see to fill it in.
 */
export const ProfileTypeBlockSchema = z.object({
    name: z.string(),
    category: ProfileTypeCategorySchema.nullable(),
    /** Tag every newly enriched profile that matches the block. */
    auto_classify: z.boolean(),
    /** The priority order of #554: the lowest wins when a profile carries several types. */
    sort_order: z.number().int(),
    conditions: z.array(BlockConditionSchema),
    updated_at: z.string().nullable(),
});
export type ProfileTypeBlock = z.infer<typeof ProfileTypeBlockSchema>;

export const ProfileTypeBlockListResponseSchema = z.array(ProfileTypeBlockSchema);
export type ProfileTypeBlockListResponse = z.infer<typeof ProfileTypeBlockListResponseSchema>;

// ── Write payloads ──

// A field with no keyword is accepted and dropped: the admin added it and has not
// filled it in yet. Whether what is left can still match anything is a business rule
// with a readable message, so it lives in the use-case, not in a `.min(1)` that would
// answer the same fault with an anonymous 400.
const FieldInputSchema = z.object({
    scope: BlockScopeSchema,
    keywords: z.array(z.string().trim().min(1).max(120)).max(500),
});

// `kind` and `company_sizes` default to a keyword condition, so a payload written
// before #724 still reads as what it meant.
const ConditionInputSchema = z.object({
    kind: BlockConditionKindSchema.default("keywords"),
    fields: z.array(FieldInputSchema).max(CLASSIFICATION_SCOPES.length),
    company_sizes: z.array(CompanySizeBandSchema).max(COMPANY_SIZE_BANDS.length).default([]),
});

/** PUT replaces the whole block: the editor edits it as one thing. */
export const SaveProfileTypeBlockRequestSchema = z.object({
    category: ProfileTypeCategorySchema,
    auto_classify: z.boolean(),
    conditions: z.array(ConditionInputSchema).max(20),
});
export type SaveProfileTypeBlockRequest = z.infer<typeof SaveProfileTypeBlockRequestSchema>;

/**
 * The type name, as it travels in the URL. It is the tag written into
 * `profiles.types` and the primary key of `profile_types`, so a comma or a semicolon
 * would read as two types wherever a list of them is joined for display.
 */
export const ProfileTypeNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[^,;]+$/, "A profile type name cannot contain a comma or a semicolon.");

// ── Preview and run ──

/** A few of the profiles a run would tag: the check a bare count cannot give. */
export const BlockMatchSampleSchema = z.object({
    name: z.string(),
    job_title: z.string().nullable(),
    company: z.string().nullable(),
});
export type BlockMatchSample = z.infer<typeof BlockMatchSampleSchema>;

/** How many sample profiles a preview carries. */
export const BLOCK_PREVIEW_SAMPLE_SIZE = 10;

export const PreviewProfileTypeBlockResponseSchema = z.object({
    tag: z.string(),
    /** Profiles the run WOULD change: already-tagged ones excluded. */
    match_count: z.number().int(),
    /**
     * Profiles the block matches that already carry the tag. Without it, a block that
     * has already been applied previews as one that matches nobody.
     */
    already_tagged_count: z.number().int(),
    sample: z.array(BlockMatchSampleSchema),
    /**
     * For each condition, in order: how many active profiles the block would match
     * WITHOUT it, tag or no tag. Set beside the block's own total, it is the measure
     * of what a condition costs: "Without it: 2,594" marks the condition doing the
     * real filtering, a figure close to the total marks one that barely narrows
     * anything. A one-condition block carries one figure: every active profile.
     */
    without_condition_counts: z.array(z.number().int()),
});
export type PreviewProfileTypeBlockResponse = z.infer<typeof PreviewProfileTypeBlockResponseSchema>;

/** Preview of a block still being written: only what the match reads travels. */
export const PreviewProfileTypeBlockDraftRequestSchema = z.object({
    name: ProfileTypeNameSchema,
    conditions: z.array(ConditionInputSchema).max(20),
});
export type PreviewProfileTypeBlockDraftRequest = z.infer<typeof PreviewProfileTypeBlockDraftRequestSchema>;

export const RunProfileTypeBlockResponseSchema = z.object({
    tag: z.string(),
    /** Profiles tagged by THIS run: a second run reports 0. */
    tagged_count: z.number().int(),
});
export type RunProfileTypeBlockResponse = z.infer<typeof RunProfileTypeBlockResponseSchema>;

// ── Removing a type from profiles (#652) ──

/**
 * Which profiles a bulk removal takes the type off.
 *
 * - `unmatched`: the profiles carrying the type that its conditions no longer match:
 *   what is left to clean up once a too-broad block has been corrected. Needs a block.
 * - `all`: every profile carrying the type, whatever put it there.
 *
 * Deactivated profiles are included in both: a profile reactivated later must not
 * come back with a type that was removed everywhere else.
 */
export const TYPE_REMOVAL_SCOPES = ["unmatched", "all"] as const;
export const TypeRemovalScopeSchema = z.enum(TYPE_REMOVAL_SCOPES);
export type TypeRemovalScope = z.infer<typeof TypeRemovalScopeSchema>;

/** What removing the type under one scope would do, without writing anything. */
export const TypeRemovalImpactSchema = z.object({
    /** Profiles that would lose the type. */
    count: z.number().int(),
    /** Of those, the ones whose primary type is this one: their chips change first. */
    primary_change_count: z.number().int(),
    /** Of those, the ones carrying no other type: they move to Other. */
    untyped_count: z.number().int(),
    /** A few of them by name: the count alone does not show a wrong selection. */
    sample: z.array(BlockMatchSampleSchema),
});
export type TypeRemovalImpact = z.infer<typeof TypeRemovalImpactSchema>;

export const PreviewTypeRemovalResponseSchema = z.object({
    tag: z.string(),
    /** Null when the type has no conditions: there is nothing to compare against. */
    unmatched: TypeRemovalImpactSchema.nullable(),
    all: TypeRemovalImpactSchema,
});
export type PreviewTypeRemovalResponse = z.infer<typeof PreviewTypeRemovalResponseSchema>;

export const RemoveTypeRequestSchema = z.object({
    scope: TypeRemovalScopeSchema,
});
export type RemoveTypeRequest = z.infer<typeof RemoveTypeRequestSchema>;

export const RemoveTypeResponseSchema = z.object({
    /** The logged removal, for the snackbar's Undo. Null when nothing was removed. */
    removal_id: z.string().nullable(),
    tag: z.string(),
    removed_count: z.number().int(),
});
export type RemoveTypeResponse = z.infer<typeof RemoveTypeResponseSchema>;

/** One logged bulk removal, as "Recent removals" lists it. */
export const TypeRemovalSchema = z.object({
    id: z.string(),
    tag: z.string(),
    scope: TypeRemovalScopeSchema,
    removed_count: z.number().int(),
    /** Display name of the admin, null once their account is gone. */
    removed_by: z.string().nullable(),
    created_at: z.string(),
    undone_at: z.string().nullable(),
    undone_by: z.string().nullable(),
});
export type TypeRemoval = z.infer<typeof TypeRemovalSchema>;

/** How many removals "Recent removals" shows. */
export const RECENT_TYPE_REMOVALS_LIMIT = 10;

export const TypeRemovalListResponseSchema = z.array(TypeRemovalSchema);
export type TypeRemovalListResponse = z.infer<typeof TypeRemovalListResponseSchema>;

export const UndoTypeRemovalResponseSchema = z.object({
    tag: z.string(),
    /** Profiles the type went back on: fewer than removed when some already carry it again. */
    restored_count: z.number().int(),
});
export type UndoTypeRemovalResponse = z.infer<typeof UndoTypeRemovalResponseSchema>;

// ── Removing a type from one profile (#652) ──

/** A type an admin took off this profile by hand: rules no longer put it back. */
export const ProfileTypeRemovalSchema = z.object({
    type: z.string(),
    /** Display name of the admin, null once their account is gone. */
    removed_by: z.string().nullable(),
    removed_at: z.string(),
});
export type ProfileTypeRemoval = z.infer<typeof ProfileTypeRemovalSchema>;

/** A type an admin added to this profile by hand (#701): a bulk removal leaves it. */
export const ProfileTypeAdditionSchema = z.object({
    type: z.string(),
    /** Display name of the admin, null once their account is gone. */
    added_by: z.string().nullable(),
    added_at: z.string(),
});
export type ProfileTypeAddition = z.infer<typeof ProfileTypeAdditionSchema>;

/**
 * A profile's types as the admin's "Types" dialog shows them: what it carries, in
 * priority order, what was taken off by hand, and which of its types were added by
 * hand (#701). A removed type the profile carries again (an import set it) is not
 * listed as removed; it is back.
 */
export const ProfileTypesEditSchema = z.object({
    types: z.array(z.string()),
    primary_type: z.string().nullable(),
    removed: z.array(ProfileTypeRemovalSchema),
    /** The types it carries that were added by hand (#701). */
    added: z.array(ProfileTypeAdditionSchema),
});
export type ProfileTypesEdit = z.infer<typeof ProfileTypesEditSchema>;

// ── Figures for the overview (#628, brought into #627) ──

/**
 * How far the types reach into the base, and how much the fields they read are worth.
 *
 * `type_counts` is who CARRIES each type today, whatever put it there (a block, a
 * manual edit or an import) because that is what a client filtering by type gets.
 * `field_fill` is there for the block author: a condition on a field filled on half
 * the profiles excludes the other half before any keyword is read.
 */
export const ProfileTypeStatsSchema = z.object({
    /** Active (not deactivated) profiles: the base every figure is a share of. */
    active_profiles: z.number().int(),
    /** Active profiles carrying at least one type of the whitelist. */
    typed_profiles: z.number().int(),
    type_counts: z.array(z.object({ name: z.string(), profile_count: z.number().int() })),
    field_fill: z.array(z.object({ scope: BlockScopeSchema, filled_profiles: z.number().int() })),
    /**
     * Active profiles whose current company has a recognised size band (#724): the
     * ceiling of a "Company size" condition. A value that normalises to no band
     * ("None-None") is not counted: the condition can never be met through it.
     */
    company_size_filled_profiles: z.number().int(),
    /**
     * The active profiles carrying SEVERAL whitelisted types, one row per set of
     * types (sorted by name), most frequent first (#628). Each profile is in exactly
     * one row, so the rows add up to the profiles with more than one type. The
     * priority order decides which of the types such a profile shows first.
     */
    type_combinations: z.array(z.object({ types: z.array(z.string()), profile_count: z.number().int() })),
});
export type ProfileTypeStats = z.infer<typeof ProfileTypeStatsSchema>;

// ── HTTP envelopes ({ success, data }) ──

export const ProfileTypeBlockListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileTypeBlockListResponseSchema,
});

export const ProfileTypeBlockDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileTypeBlockSchema,
});

export const PreviewProfileTypeBlockDetailResponseSchema = z.object({
    success: z.boolean(),
    data: PreviewProfileTypeBlockResponseSchema,
});

export const RunProfileTypeBlockDetailResponseSchema = z.object({
    success: z.boolean(),
    data: RunProfileTypeBlockResponseSchema,
});

export const ProfileTypeStatsDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileTypeStatsSchema,
});

export const PreviewTypeRemovalDetailResponseSchema = z.object({
    success: z.boolean(),
    data: PreviewTypeRemovalResponseSchema,
});

export const RemoveTypeDetailResponseSchema = z.object({
    success: z.boolean(),
    data: RemoveTypeResponseSchema,
});

export const TypeRemovalListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: TypeRemovalListResponseSchema,
});

export const UndoTypeRemovalDetailResponseSchema = z.object({
    success: z.boolean(),
    data: UndoTypeRemovalResponseSchema,
});

export const ProfileTypesEditDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ProfileTypesEditSchema,
});

// ── A client's own types on one profile (#723) ──

/**
 * A profile's types as a client's "Types" dialog shows them, for the caller's
 * licence: what the licence sees (Invyo's and its own, merged in priority order),
 * which of them Invyo set (not removable), and the ones the licence added,
 * the only ones it may remove. A type the licence added that Invyo carries too
 * is Invyo's here.
 */
export const LicenceProfileTypesEditSchema = z.object({
    types: z.array(z.string()),
    primary_type: z.string().nullable(),
    invyo_types: z.array(z.string()),
    added: z.array(ProfileTypeAdditionSchema),
});
export type LicenceProfileTypesEdit = z.infer<typeof LicenceProfileTypesEditSchema>;

export const LicenceProfileTypesEditDetailResponseSchema = z.object({
    success: z.boolean(),
    data: LicenceProfileTypesEditSchema,
});
